import { ConfigValidator } from './config-validator.js'
import { ModelLoader } from './model-loader.js'
import { SceneManager } from './scene-manager.js'
import { KeycapRenderer } from './keycap-renderer.js'
import { KeyboardAssembler } from './keyboard-assembler.js'
import { parseSwitchPositions, loadRotationData } from '../switch-position-parser.js'
import { mapBoardKeymapToMatrixNames } from '../keycap-legend-mapper.js'

export class KeyboardScene {
  constructor (container, config) {
    this.container = container
    this.rawConfig = config
    this.config = null

    this.configValidator = null
    this.modelLoader = new ModelLoader()
    this.sceneManager = new SceneManager(container)
    this.keycapRenderer = new KeycapRenderer()
    this.keyboardAssembler = new KeyboardAssembler(this.keycapRenderer)

    this.keyboardGroup = null
    this.caseModel = null
    this.boardModel = null
    this.splitData = null
    this.models = null
    this.switchPositions = []
    this.rotationData = null
  }

  async init () {
    await this.validateAndPrepareConfig()
    await this.loadData()

    await this.loadModels()
    this.clearContainer()
    this.sceneManager.init()
    this.assembleKeyboard()
    this.fitCameraToKeyboard()
    this.sceneManager.render()
  }

  clearContainer () {
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild)
    }
  }

  async validateAndPrepareConfig () {
    this.configValidator = new ConfigValidator(this.rawConfig)
    this.config = await this.configValidator.validate()

    this.config.transformedKeyMap = mapBoardKeymapToMatrixNames(
      this.config.keyMap,
      this.config.rows,
      this.config.columns * 2
    )
  }

  async loadData () {
    const [switchPositions, rotationData] = await Promise.all([
      parseSwitchPositions(this.config.yamlPath),
      loadRotationData(this.config.rotationPath)
    ])

    if (switchPositions.length === 0) {
      throw new Error('No valid switch positions found in YAML file')
    }

    this.switchPositions = switchPositions
    this.rotationData = rotationData
  }

  async loadModels () {
    this.models = await this.modelLoader.loadAll(this.config)
  }

  assembleKeyboard () {
    // All keyboards are split, so always use split keyboard logic
    const splitResult = this.keyboardAssembler.createSplitKeyboard(
      this.models,
      this.switchPositions,
      this.rotationData,
      this.config
    )

    this.splitData = splitResult
    this.keyboardGroup = splitResult.splitKeyboardGroup
    this.caseModel = [splitResult.leftHalf.caseModel, splitResult.rightHalf.caseModel]
    this.boardModel = [splitResult.leftHalf.boardModel, splitResult.rightHalf.boardModel]

    this.sceneManager.add(this.keyboardGroup)
  }

  setKeycapState ({ color, visible = true, showLegends, legendColor }) {
    if (!this.sceneManager.scene) {
      throw new Error('Scene not initialized')
    }

    this.sceneManager.scene.traverse((child) => {
      if (child.userData && child.userData.isKeycap && child.isMesh) {
        child.visible = visible

        if (color !== undefined || showLegends !== undefined || legendColor !== undefined) {
          const legend = this.keyboardAssembler.getKeycapLegend(
            child.userData.originalPosition,
            this.config.transformedKeyMap
          )

          const newMaterial = this.keycapRenderer.createKeycapMaterial(legend, {
            keycapColor: color !== undefined ? color : child.material.color.getHex(),
            legendColor: legendColor !== undefined ? legendColor : undefined,
            showLegends: showLegends !== undefined ? showLegends : true
          })

          child.material.dispose()
          child.material = newMaterial
        }
      }
    })

    this.sceneManager.render()
  }

  setCaseState ({ color, visible = true }) {
    if (!this.caseModel) {
      throw new Error('Case model not loaded')
    }

    // Handle split keyboard case models (always an array)
    this.caseModel.forEach(caseModel => {
      caseModel.visible = visible
      if (color !== undefined) {
        caseModel.material.color.setHex(color)
      }
    })

    this.sceneManager.render()
  }

  setSwitchState ({ stemColor, baseColor, visible = true }) {
    if (!this.sceneManager.scene) {
      throw new Error('Scene not initialized')
    }

    this.sceneManager.scene.traverse((child) => {
      if (child.userData && child.userData.isSwitch) {
        child.visible = visible

        if (stemColor !== undefined || baseColor !== undefined) {
          let partIndex = 0
          child.traverse((part) => {
            if (part.isMesh && part.material) {
              if (partIndex === 1 && baseColor !== undefined) {
                part.material.color.setHex(baseColor)
              } else if (partIndex === 2 && stemColor !== undefined) {
                part.material.color.setHex(stemColor)
              }
              partIndex++
            }
          })
        }
      }
    })

    this.sceneManager.render()
  }

  fitCameraToKeyboard () {
    if (this.keyboardGroup) {
      this.sceneManager.fitCameraToKeyboard(this.keyboardGroup)
    }
  }

  render () {
    this.sceneManager.render()
  }

  dispose () {
    if (this.keycapRenderer) {
      this.keycapRenderer.dispose()
    }

    if (this.sceneManager) {
      this.sceneManager.dispose()
    }
  }
}
